import type { GameId } from "@bing/shared";
import type { ResolvedTheme } from "../settings/prefs";
import darkAll from "./games-blur/dark/all.jpg";
import darkBackgammon from "./games-blur/dark/backgammon.jpg";
import darkBluff from "./games-blur/dark/bluff.jpg";
import darkChess from "./games-blur/dark/chess.jpg";
import darkDooz from "./games-blur/dark/dooz.jpg";
import darkHearts from "./games-blur/dark/hearts.jpg";
import darkHokm from "./games-blur/dark/hokm.jpg";
import darkMensch from "./games-blur/dark/mensch.jpg";
import darkPasur from "./games-blur/dark/pasur.jpg";
import darkShelem from "./games-blur/dark/shelem.jpg";
import lightAll from "./games-blur/light/all.jpg";
import lightBackgammon from "./games-blur/light/backgammon.jpg";
import lightBluff from "./games-blur/light/bluff.jpg";
import lightChess from "./games-blur/light/chess.jpg";
import lightDooz from "./games-blur/light/dooz.jpg";
import lightHearts from "./games-blur/light/hearts.jpg";
import lightHokm from "./games-blur/light/hokm.jpg";
import lightMensch from "./games-blur/light/mensch.jpg";
import lightPasur from "./games-blur/light/pasur.jpg";
import lightShelem from "./games-blur/light/shelem.jpg";

/**
 * The «برترین‌ها» band's art — BLURRED **AND DARKENED** AT BUILD TIME, so the device has nothing to
 * do with it but composite an ordinary opaque picture. See `apps/web/tools/bg-art/make.cjs`.
 *
 * ⚠️ **THERE IS NO RUNTIME `filter` AND NO RUNTIME OPACITY BLEND.** The blur is baked (a runtime
 * one is GPU work on every device that composites the layer, and its cost could not be measured on
 * this host at all in four harness configurations, `probe-bg-cost.cjs`); the darkening is baked
 * too, which is why there are TWO SETS. `art × α over the page colour` only makes sense on the
 * page it was baked for. Both numbers come out of `theme.css` — `--c-background` and
 * `--lb-art-opacity` — so the operator's tuning point is still one place.
 *
 * ⚠️ **SO THE ART MUST FOLLOW `<html data-theme>`, WHICH IS THE ATTRIBUTE THE PAGE COLOUR ITSELF
 * FOLLOWS.** Not `getResolvedTheme()`: that is module state, and anything which stamps the
 * attribute directly (every probe in this repo does) would leave dark art on a light page.
 *
 * ⚠️ **A DOWNSCALE IS NOT A BLUR, AND THIS PIPELINE HAS FAILED IN BOTH DIRECTIONS.** It began as
 * the lobby tile box-averaged to 48×36, on the reasoning that throwing away the detail is what a
 * `blur()` does — it is half of it; the other half is the reconstruction on the way back up, and
 * the browser's is BILINEAR, so the samples were 8.1 CSS px apart and the page showed a mosaic.
 * The correction over-shot twice: gaussian σ=8 at 160×120 was smooth and unrecognisable, «reads as
 * low quality, not as a soft background», and σ=3 at 420×315 was STILL soft because a phone paints
 * this band at 854–1281 DEVICE px and a 420px picture is upscaled 2–3× on top of its own blur. It
 * is **1024×768, σ=2, q90** now.
 *
 * ⚠️ **THESE ARE EMITTED ASSETS, NOT `data:` URIs — 35–66 KB EACH, 1,049,416 BYTES FOR THE
 * TWENTY**, well over vite's 4096-byte inline limit, so they leave the bundle and are fetched on
 * demand. That is the trade for the resolution: the band can now arrive after the board on a slow
 * link. In exchange only the art actually LOOKED AT is ever downloaded, and `/assets/` serves them
 * content-hashed with `public, immutable`.
 *
 * ⚠️ **THAT CACHING IS MEASURED, NOT ASSUMED — `/assets/` ANSWERS WITH _TWO_ `Cache-Control`
 * HEADERS** (`expires 1y` emits its own beside the `add_header` one), and this app has already lost
 * a policy to exactly that pairing on `/decks/`. Measured in Chromium on the deployed staging build
 * (`apps/web/tools/leaderboard-probes/probe-art-cache.cjs`, 8/8): a walk of all 7 reachable games
 * costs **367 KB once**; every later tap on a game already looked at costs **0 bytes** (disk-cache),
 * and so does a full reload — against a cache-busted control of the SAME file that DOES transfer
 * 35,700 B. ⚠️ It is ONE THEME's seven files: flipping the theme fetches the other set.
 *
 * «همه» is not a game and has no lobby tile: it takes 🏆 on the app's own field green, composed
 * HIGH in its frame so the cup lands in the open strip between the chips and the podium rather
 * than behind three avatars. It used to take `logo.jpg` — a brand mark where the other nine carry
 * a photograph of the thing the board is about.
 */
export const GAME_ART_BLUR: Readonly<
  Record<ResolvedTheme, Readonly<Record<GameId | "all", string>>>
> = {
  dark: {
    hearts: darkHearts,
    hokm: darkHokm,
    shelem: darkShelem,
    pasur: darkPasur,
    bluff: darkBluff,
    chess: darkChess,
    backgammon: darkBackgammon,
    mensch: darkMensch,
    dooz: darkDooz,
    all: darkAll,
  },
  light: {
    hearts: lightHearts,
    hokm: lightHokm,
    shelem: lightShelem,
    pasur: lightPasur,
    bluff: lightBluff,
    chess: lightChess,
    backgammon: lightBackgammon,
    mensch: lightMensch,
    dooz: lightDooz,
    all: lightAll,
  },
};
